import { useEffect, useMemo, useState } from 'react';

import { Delete02Icon } from '@hugeicons/core-free-icons';
import { AdminPermissions } from 'constants/admin-permissions';
import { countries } from 'constants/countries';
import { getFetchBaseQueryErrorMessage } from 'helpers/api';
import patternValidators from 'helpers/validators/pattern-validators';
import { useAccessControl } from 'hooks/access-control';
import { useSelector } from 'react-redux';

import Alert from 'components/Alert/Alert';
import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { Button, Input, InputGroup, MultipleSelect, Select } from 'components/FormComponents';
import { Table } from 'components/Table/Table';

import { selectAuthentication } from 'api/authentication-slice';
import { useGetAllCompaniesListQuery } from 'api/company-api';
import {
  useCreateCompanyGroupMutation,
  useDeleteCompanyGroupMutation,
  useGetCompanyGroupQuery,
  useGetCompanyGroupsQuery,
  useUpdateCompanyGroupMutation,
} from 'api/company-groups-api';

const emptyDraft = () => ({
  name: '',
  invoice_style: 'separate',
  invoice_entity_name: '',
  email: '',
  region: '',
  post_code: '',
  country: '',
  company_ids: [],
  parent_company_id: '',
});

const GROUPS_PER_PAGE = 15;

const invoiceLabel = style => (style === 'separate' ? 'Invoice per company' : 'One invoice');

const localityLabel = country => {
  if (country === 'Australia') return 'State/Territory';
  if (country === 'United States of America') return 'State';
  if (country === 'New Zealand') return 'City/Town';
  return 'Town/City';
};

const AdminCompanyGroups = ({ createSignal = 0, onLoadingChange = () => {} }) => {
  const authentication = useSelector(selectAuthentication);
  const hasPermission = useAccessControl(authentication.admin_permissions);
  const canEdit = hasPermission(AdminPermissions.UpdateCompany);

  const { data: companyGroups = [], isLoading } = useGetCompanyGroupsQuery();
  const { data: companiesData, refetch: refetchCompanies } = useGetAllCompaniesListQuery();
  const companies = companiesData?.result ?? [];

  const [editorId, setEditorId] = useState(undefined);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [listQuery, setListQuery] = useState({ page: 1, search: '' });

  const editorOpen = editorId !== undefined;

  useEffect(() => {
    if (editorOpen) refetchCompanies();
  }, [editorOpen, editorId, refetchCompanies]);

  const { data: companyGroup, isFetching: isLoadingCompanyGroup } = useGetCompanyGroupQuery(
    editorId,
    {
      skip: !editorId,
    },
  );
  const [createCompanyGroup, { isLoading: isCreating }] = useCreateCompanyGroupMutation();
  const [updateCompanyGroup, { isLoading: isUpdating }] = useUpdateCompanyGroupMutation();
  const [deleteCompanyGroup, { isLoading: isDeleting }] = useDeleteCompanyGroupMutation();

  useEffect(() => {
    if (!companyGroup || companyGroup.id !== editorId || isLoadingCompanyGroup) return;

    setDraft(current => {
      if (current?.id === companyGroup.id) return current;
      return {
        id: companyGroup.id,
        name: companyGroup.name || '',
        invoice_style: companyGroup.invoice_style || 'separate',
        invoice_entity_name: companyGroup.invoice_entity_name || '',
        email: companyGroup.email || '',
        region: companyGroup.region || '',
        post_code: companyGroup.post_code || '',
        country: companyGroup.country || '',
        company_ids: (companyGroup.companies || []).map(company => String(company.id)),
        parent_company_id: companyGroup.parent_company_id
          ? String(companyGroup.parent_company_id)
          : '',
      };
    });
  }, [companyGroup, editorId, isLoadingCompanyGroup]);

  useEffect(() => {
    onLoadingChange(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (!createSignal) return;
    setError('');
    setShowErrors(false);
    setDraft(emptyDraft());
    setEditorId(null);
  }, [createSignal]);

  const closeEditor = () => {
    setEditorId(undefined);
    setDraft(null);
    setError('');
    setShowErrors(false);
    setShowDeleteAlert(false);
  };

  const run = async action => {
    setError('');
    try {
      return await action();
    } catch (saveError) {
      setError(getFetchBaseQueryErrorMessage(saveError) || 'The company group could not be saved.');
      return null;
    }
  };

  const billingEmail = (draft?.email || '').trim();
  const billingEmailError = !draft
    ? ''
    : !billingEmail
      ? 'Billing email is required.'
      : patternValidators.email.pattern.value.test(billingEmail)
        ? ''
        : patternValidators.email.pattern.message;
  const missingDetails = draft
    ? [
        !(draft.name || '').trim() && 'Company group name is required.',
        !(draft.invoice_entity_name || '').trim() && 'Billing name is required.',
        billingEmailError,
        draft.company_ids.length < 2 && 'Choose at least two companies.',
        !draft.parent_company_id && 'Choose a parent company.',
      ].filter(Boolean)
    : [];
  const formError = showErrors ? missingDetails.join(' ') : error;
  const locality = localityLabel(draft?.country);
  const currentGroupId = draft?.id ? Number(draft.id) : null;
  const selectableCompanies = companies.filter(company => {
    const groupId = company.company_group_id ? Number(company.company_group_id) : null;
    return !groupId || groupId === currentGroupId;
  });

  const saveCompanyGroup = () => {
    if (missingDetails.length > 0) {
      setShowErrors(true);
      setError('');
      return;
    }

    setShowErrors(false);
    run(async () => {
      const body = {
        name: draft.name,
        invoice_style: draft.invoice_style,
        invoice_entity_name: draft.invoice_entity_name,
        email: billingEmail,
        region: draft.region,
        post_code: draft.post_code,
        country: draft.country,
        company_ids: draft.company_ids.map(id => Number(id)),
        parent_company_id: Number(draft.parent_company_id),
      };

      if (draft.id) {
        await updateCompanyGroup({ id: draft.id, ...body }).unwrap();
        closeEditor();
        return;
      }

      await createCompanyGroup(body).unwrap();
      closeEditor();
    });
  };

  const removeCompanyGroup = () => {
    setShowDeleteAlert(false);
    run(async () => {
      await deleteCompanyGroup(draft.id).unwrap();
      closeEditor();
    });
  };

  const openGroup = id => {
    setError('');
    setShowErrors(false);
    setDraft(null);
    setEditorId(id);
  };

  const visibleGroups = useMemo(() => {
    const search = listQuery.search.trim().toLowerCase();
    return companyGroups.filter(item => {
      if (!search) return true;
      return [item.name, item.invoice_entity_name, item.email, item.id]
        .filter(value => value != null && value !== '')
        .some(value => String(value).toLowerCase().includes(search));
    });
  }, [companyGroups, listQuery.search]);

  const totalRecords = visibleGroups.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / GROUPS_PER_PAGE));
  const page = Math.min(listQuery.page, totalPages);
  const pageGroups = visibleGroups.slice((page - 1) * GROUPS_PER_PAGE, page * GROUPS_PER_PAGE);

  return (
    <>
      <Table.Context
        onQueryUpdate={params =>
          setListQuery({
            page: Number(params.page) || 1,
            search: params.search || '',
          })
        }
        defaultQuery={{ page: 1 }}>
        <Table.TableContent>
          <Table.Filters>
            <Table.LeftFilters>
              <Table.Search placeholder="Search for a company group..." />
            </Table.LeftFilters>
          </Table.Filters>
          <Table className="u-margin--large">
            <Table.Head>
              <Table.Row>
                <Table.Header>ID</Table.Header>
                <Table.Header>Name</Table.Header>
                <Table.Header>Companies</Table.Header>
                <Table.Header>Invoices</Table.Header>
                <Table.Header>Billing email</Table.Header>
                <Table.Header>Country</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {pageGroups.length === 0 && (
                <Table.Placeholder>
                  {companyGroups.length === 0
                    ? 'No company groups yet.'
                    : 'No company groups found.'}
                </Table.Placeholder>
              )}
              {pageGroups.map(item => (
                <Table.Row key={item.id}>
                  <Table.Col>
                    <button
                      type="button"
                      className="u-link dark border-0 bg-transparent p-0"
                      onClick={() => openGroup(item.id)}>
                      {item.id}
                    </button>
                  </Table.Col>
                  <Table.Col>
                    <button
                      type="button"
                      className="u-link dark border-0 bg-transparent p-0"
                      onClick={() => openGroup(item.id)}>
                      {item.name}
                    </button>
                  </Table.Col>
                  <Table.Col>{item.company_count}</Table.Col>
                  <Table.Col>{invoiceLabel(item.invoice_style)}</Table.Col>
                  <Table.Col>{item.email}</Table.Col>
                  <Table.Col>{item.country}</Table.Col>
                </Table.Row>
              ))}
            </Table.Body>
            <Table.Pagination
              page={page}
              records_per_page={GROUPS_PER_PAGE}
              total_records={totalRecords}
              total_pages={totalRecords === 0 ? 0 : totalPages}
            />
          </Table>
        </Table.TableContent>
      </Table.Context>

      <AnimatedModal
        title={draft?.id ? draft.name || 'Company group' : 'New company group'}
        description="All invoices are addressed to the company group."
        visible={editorOpen}
        onClose={closeEditor}
        large>
        {editorId && isLoadingCompanyGroup && !draft && <p>Loading company group…</p>}
        {draft && (
          <div>
            <InputGroup title="Company group name">
              <Input
                name="name"
                placeholder="Acme group"
                error={
                  showErrors && !(draft.name || '').trim() ? 'Company group name is required.' : ''
                }
                inputProps={{
                  value: draft.name,
                  disabled: !canEdit,
                  onChange: event => setDraft({ ...draft, name: event.target.value }),
                }}
              />
            </InputGroup>
            <InputGroup title="Companies">
              <MultipleSelect
                value={draft.company_ids}
                disabled={!canEdit}
                useSearch
                placeholder="Choose at least two companies"
                error={
                  showErrors && draft.company_ids.length < 2 ? 'Choose at least two companies.' : ''
                }
                onChange={companyIds =>
                  setDraft(current => ({
                    ...current,
                    company_ids: companyIds,
                    parent_company_id: companyIds.includes(String(current.parent_company_id))
                      ? current.parent_company_id
                      : '',
                  }))
                }>
                {selectableCompanies.map(company => (
                  <MultipleSelect.Item key={company.id} value={String(company.id)}>
                    {company.name}
                  </MultipleSelect.Item>
                ))}
              </MultipleSelect>
            </InputGroup>
            <InputGroup title="Parent company">
              <Select
                name="parent_company_id"
                value={draft.parent_company_id}
                disabled={!canEdit}
                onChange={event => setDraft({ ...draft, parent_company_id: event.target.value })}
                useDefault
                error={showErrors && !draft.parent_company_id ? 'Choose a parent company.' : ''}>
                {companies
                  .filter(company => draft.company_ids.includes(String(company.id)))
                  .map(company => (
                    <Select.Item key={company.id} value={String(company.id)}>
                      {company.name}
                    </Select.Item>
                  ))}
              </Select>
              <p className="t-small secondary-text u-margin-top--small">
                This company can view billing reports.
              </p>
            </InputGroup>
            <InputGroup title="Invoices">
              <Select
                name="invoice_style"
                value={draft.invoice_style}
                disabled={!canEdit}
                onChange={event => setDraft({ ...draft, invoice_style: event.target.value })}
                useDefault>
                <Select.Item value="combined">One invoice for the company group</Select.Item>
                <Select.Item value="separate">
                  One invoice per company, addressed to the company group
                </Select.Item>
              </Select>
            </InputGroup>
            <InputGroup title="Billing name">
              <Input
                name="invoice_entity_name"
                error={
                  showErrors && !(draft.invoice_entity_name || '').trim()
                    ? 'Billing name is required.'
                    : ''
                }
                inputProps={{
                  value: draft.invoice_entity_name,
                  disabled: !canEdit,
                  onChange: event =>
                    setDraft({ ...draft, invoice_entity_name: event.target.value }),
                }}
              />
            </InputGroup>
            <InputGroup title="Billing email">
              <Input
                name="email"
                error={
                  billingEmail && !patternValidators.email.pattern.value.test(billingEmail)
                    ? patternValidators.email.pattern.message
                    : showErrors
                      ? billingEmailError
                      : ''
                }
                inputProps={{
                  value: draft.email,
                  disabled: !canEdit,
                  onChange: event => setDraft({ ...draft, email: event.target.value }),
                }}
              />
            </InputGroup>
            <InputGroup title="Country">
              <Select
                name="country"
                value={draft.country}
                disabled={!canEdit}
                onChange={event => setDraft({ ...draft, country: event.target.value })}
                useDefault>
                {countries.map(country => (
                  <Select.Item key={country.value} value={country.value}>
                    {country.name}
                  </Select.Item>
                ))}
              </Select>
            </InputGroup>
            <div className="inline">
              <InputGroup title={locality}>
                <Input
                  name="region"
                  placeholder={`Enter ${locality.toLowerCase()}`}
                  inputProps={{
                    value: draft.region,
                    disabled: !canEdit,
                    onChange: event => setDraft({ ...draft, region: event.target.value }),
                  }}
                />
              </InputGroup>
              <InputGroup title="Postcode">
                <Input
                  name="post_code"
                  placeholder="Enter postcode"
                  inputProps={{
                    value: draft.post_code,
                    disabled: !canEdit,
                    onChange: event => setDraft({ ...draft, post_code: event.target.value }),
                  }}
                />
              </InputGroup>
            </div>

            {canEdit && (
              <div className="u-margin-top--large">
                {formError && <p className="t-small text-error">{formError}</p>}
                <div className="d-flex justify-content-between">
                  {draft.id ? (
                    <Button
                      type="secondary"
                      disabled={isDeleting}
                      onClick={() => setShowDeleteAlert(true)}>
                      Delete company group
                    </Button>
                  ) : (
                    <span />
                  )}
                  <Button
                    disabled={missingDetails.length > 0 || isCreating || isUpdating}
                    onClick={saveCompanyGroup}>
                    {draft.id ? 'Save company group' : 'Create company group'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </AnimatedModal>
      <Alert
        show={showDeleteAlert}
        title={`Are you sure you want to delete "${draft?.name || 'this company group'}"?`}
        message="Its companies will manage their own billing again. Deletion cannot be undone."
        ok="Delete"
        cancel="Cancel"
        type="error"
        icon={Delete02Icon}
        onOk={removeCompanyGroup}
        onCancel={() => setShowDeleteAlert(false)}
      />
    </>
  );
};

export default AdminCompanyGroups;
